import {
  BEST_OF, MAX_PLAYERS, MIN_PLAYERS, MODE_NAMES, TIME_LIMITS, XP_SCALES,
  checkSettings, defaultSettings, formatClockPair, joinable, playableMaps,
  type LobbySettings, type LobbySummary,
} from '../core/lobby.ts';

/**
 * The first thing anybody sees.
 *
 * Two tabs and a list, which is the shape every server browser has had since
 * Counter-Strike because it is the right one: the question a player arrives with
 * is "is there a game on", and everything else is secondary to answering it in
 * one screen without a click.
 *
 * Deliberately knows nothing about how lobbies are fetched or created. It is
 * handed a way to list and a way to create, so the same screen works against a
 * stub today and a server later — which is the only way to build a browser
 * before there is anything to browse.
 */

/** Graphics, as the screen needs to show them. Named here so the UI stays dumb. */
export interface GraphicsHooks {
  /** The tier in force, whether chosen or guessed. */
  tier: 'low' | 'medium' | 'high';
  /** Why that one — the GPU, or the fact that somebody picked it. */
  reason: string;
  /** Remembered, and in force for the match about to start. */
  choose(tier: 'low' | 'medium' | 'high'): void;
}

export interface MainScreenHooks {
  /** Current lobbies. Polled while the Join tab is open. */
  list(): Promise<LobbySummary[]>;
  join(id: string): void;
  create(settings: LobbySettings): void;
  playerName: string;
  /** Handed a way to close the screen, for the caller to keep. */
  dismiss?(close: () => void): void;
  /** Absent on a screen with no renderer behind it, as in tests. */
  graphics?: GraphicsHooks;
}

declare const __BUILD__: string;

const el = <T extends HTMLElement>(tag: string, cls?: string, text?: string): T => {
  const node = document.createElement(tag) as T;
  if (cls) node.className = cls;
  if (text !== undefined) node.textContent = text;
  return node;
};

/** How often the list refreshes while it is being looked at. */
const POLL_SECONDS = 3;

export function runMainScreen(root: HTMLElement, hooks: MainScreenHooks): void {
  root.hidden = false;
  root.innerHTML = '';
  root.classList.add('main-screen');

  root.append(el('h1', 'main-title', 'Mage Wars'));

  // Small, in the corner, and the fastest way to tell a stale bundle from a bug.
  const stamp = el('div', 'main-build');
  stamp.textContent = typeof __BUILD__ === 'string' ? __BUILD__ : 'dev';
  root.append(stamp);

  const tabs = el('div', 'main-tabs');
  const joinTab = el<HTMLButtonElement>('button', 'main-tab', 'Join Match');
  const createTab = el<HTMLButtonElement>('button', 'main-tab', 'Create Lobby');
  tabs.append(joinTab, createTab);
  root.append(tabs);

  const panel = el('div', 'main-panel');
  root.append(panel);

  let poll: number | undefined;
  let stopPolling = (): void => { /* set once the list is up */ };
  const show = (which: 'join' | 'create'): void => {
    joinTab.classList.toggle('is-on', which === 'join');
    createTab.classList.toggle('is-on', which === 'create');
    window.clearInterval(poll);
    panel.innerHTML = '';

    if (which === 'join') {
      const list = buildList(panel, hooks);
      void list.refresh();
      poll = window.setInterval(() => void list.refresh(), POLL_SECONDS * 1000);
      // Stopped when the screen goes away, or it keeps asking a server for
      // lobbies through a match nobody wants interrupted.
      stopPolling = () => window.clearInterval(poll);
    } else {
      buildCreate(panel, hooks);
    }
  };

  joinTab.addEventListener('click', () => show('join'));
  createTab.addEventListener('click', () => show('create'));
  show('join');

  // Below the panel and outside the tabs, because it belongs to neither: the
  // setting is about this machine rather than about the match being arranged.
  if (hooks.graphics) root.append(buildGraphics(hooks.graphics));

  /** Takes the screen down for good, and stops it asking for lobbies. */
  hooks.dismiss?.(() => {
    stopPolling();
    root.hidden = true;
    root.innerHTML = '';
  });
}

// ── Graphics ─────────────────────────────────────────────────────────────────

const TIER_LABELS: Record<GraphicsHooks['tier'], string> = {
  low: 'Low — best frame rate',
  medium: 'Medium',
  high: 'High — best looking',
};

/**
 * The graphics tier, on the screen everybody passes through.
 *
 * It lives here rather than in an options menu because of when it can be
 * changed. Two of the things a tier decides cannot be altered once a match is
 * running: the number of torch lights is compiled into every lit material's
 * shader, and the shadow map is an allocated texture. Changing either mid-match
 * means recompiling the whole arena, which is exactly the stall the loading
 * screen exists to hide. This screen runs *before* any of that is built, so a
 * choice made here simply is the setting — no reload, no warning, no stall.
 *
 * The guess is shown rather than hidden. Detection reads the GPU string and the
 * pixel count and is right often enough to be worth having, but it is a guess,
 * and somebody whose machine has been put on Low deserves to know that happened
 * and what it was reasoning from.
 */
function buildGraphics(graphics: GraphicsHooks): HTMLElement {
  const wrap = el('div', 'main-graphics');

  const label = el('label', 'graphics-field');
  label.append(el('span', 'graphics-label', 'Graphics'));

  const select = el<HTMLSelectElement>('select', 'graphics-select');
  for (const tier of ['low', 'medium', 'high'] as const) {
    const option = el<HTMLOptionElement>('option', undefined, TIER_LABELS[tier]);
    option.value = tier;
    if (tier === graphics.tier) option.selected = true;
    select.append(option);
  }

  const note = el('span', 'graphics-note', graphics.reason);
  select.addEventListener('change', () => {
    const tier = select.value as GraphicsHooks['tier'];
    graphics.choose(tier);
    // The reason is no longer the detector's; it is the player's.
    note.textContent = 'your choice, remembered for next time';
  });

  label.append(select);
  wrap.append(label, note);
  return wrap;
}

// ── Join ─────────────────────────────────────────────────────────────────────

function buildList(panel: HTMLElement, hooks: MainScreenHooks): { refresh(): Promise<void> } {
  const table = el('div', 'lobby-list');
  const head = el('div', 'lobby-row lobby-head');
  for (const [cls, label] of [
    ['col-name', 'Lobby'], ['col-count', 'Players'], ['col-map', 'Map'],
    ['col-xp', 'XP'], ['col-rounds', 'Rounds'], ['col-clock', 'Time'],
    ['col-join', ''],
  ] as const) {
    head.append(el('span', cls, label));
  }
  table.append(head);

  const body = el('div', 'lobby-body');
  table.append(body);
  panel.append(table);

  const empty = el('p', 'lobby-empty', 'No lobbies open. Create one.');

  return {
    async refresh(): Promise<void> {
      const rows = await hooks.list();
      body.innerHTML = '';
      empty.remove();

      if (!rows.length) {
        panel.append(empty);
        return;
      }

      for (const row of rows) {
        const line = el('div', 'lobby-row');
        line.append(el('span', 'col-name', row.name));
        /*
         * Marked full or joinable on the row rather than only on the button, so
         * the state is legible while scanning rather than only on reaching for
         * it — which is the whole job of a list like this.
         */
        line.dataset.full = String(!joinable(row));

        line.append(el('span', 'col-count', `${row.players}/${row.maxPlayers}`));
        // The map carries the mode, so naming both would be saying it twice.
        line.append(el('span', 'col-map', `${row.map}`));
        line.append(el('span', 'col-xp', `${Math.round(row.xpScale * 100)}%`));
        // A single round is not a series and should not be dressed as one.
        line.append(el('span', 'col-rounds',
          row.bestOf > 1 ? `Best of ${row.bestOf}` : 'Single'));
        line.append(el('span', 'col-clock', row.inWarmup
          ? 'Warm-up'
          : formatClockPair(row.elapsed, row.limit)));

        const go = el<HTMLButtonElement>('button', 'col-join lobby-join', 'Join');
        go.disabled = !joinable(row);
        go.addEventListener('click', () => hooks.join(row.id));
        line.append(go);

        line.title = MODE_NAMES[row.mode];
        body.append(line);
      }
    },
  };
}

// ── Create ───────────────────────────────────────────────────────────────────

function buildCreate(panel: HTMLElement, hooks: MainScreenHooks): void {
  const draft: LobbySettings = defaultSettings(hooks.playerName);

  const form = el('div', 'lobby-form');
  panel.append(form);

  const field = (label: string, control: HTMLElement): void => {
    const wrap = el('label', 'lobby-field');
    wrap.append(el('span', 'lobby-label', label), control);
    form.append(wrap);
  };

  const name = el<HTMLInputElement>('input', 'lobby-name');
  name.type = 'text';
  name.value = draft.name;
  name.maxLength = 40;
  field('Lobby name', name);

  /*
   * The map pool is checkboxes rather than a dropdown because it is a *set*, and
   * because the mode comes with the map — so this list is also where a player
   * learns what they will be playing. Naming the mode beside each map is the
   * only place that binding is visible.
   */
  const pool = el('div', 'lobby-pool');
  for (const map of playableMaps()) {
    const row = el('label', 'lobby-map');
    const box = el<HTMLInputElement>('input');
    box.type = 'checkbox';
    box.checked = draft.mapPool.includes(map.id);
    box.addEventListener('change', () => {
      draft.mapPool = box.checked
        ? [...draft.mapPool, map.id]
        : draft.mapPool.filter((m) => m !== map.id);
    });
    row.append(box, el('span', 'lobby-map-name', map.name),
      el('span', 'lobby-map-mode', MODE_NAMES[map.mode]));
    pool.append(row);
  }
  field('Map pool', pool);

  const choose = (
    options: readonly number[], value: number, render: (n: number) => string,
  ): HTMLSelectElement => {
    const select = el<HTMLSelectElement>('select', 'lobby-select');
    for (const option of options) {
      const node = el<HTMLOptionElement>('option', undefined, render(option));
      node.value = String(option);
      if (option === value) node.selected = true;
      select.append(node);
    }
    return select;
  };

  const bestOf = choose(BEST_OF, draft.bestOf, (n) => `Best of ${n}`);
  bestOf.addEventListener('change', () => { draft.bestOf = Number(bestOf.value); });
  field('Rounds per map', bestOf);

  const sizes = Array.from(
    { length: MAX_PLAYERS - MIN_PLAYERS + 1 }, (_, i) => MIN_PLAYERS + i,
  );
  const cap = choose(sizes, draft.maxPlayers, (n) => String(n));
  cap.addEventListener('change', () => { draft.maxPlayers = Number(cap.value); });
  field('Max players', cap);

  const xp = choose(XP_SCALES, draft.xpScale, (n) => `${Math.round(n * 100)}%`);
  xp.addEventListener('change', () => { draft.xpScale = Number(xp.value); });
  field('Experience', xp);

  const time = choose(TIME_LIMITS, draft.timeLimit, (n) => `${n} minutes`);
  time.addEventListener('change', () => { draft.timeLimit = Number(time.value); });
  field('Time limit', time);

  /*
   * What a mage keeps between rounds, as a choice rather than a rule.
   *
   * Carrying is the default and is the game this is: levels are spell tiers, and
   * a fifteen-minute round is barely long enough to see the middle of a list.
   * Across a best-of-five the side that took the first round comes into the
   * second already able to cast things the other two cannot, which snowballs —
   * and that is the point, not a flaw.
   *
   * Resetting is a different game and worth being able to pick. Every round
   * starts level, the set is decided by play rather than by who got ahead first,
   * and nobody joining on round four is thirteen levels behind. Neither is
   * correct; the host should get to say which one they are running.
   */
  const carry = el<HTMLSelectElement>('select', 'lobby-select');
  for (const [value, label] of [
    ['carry', 'Carry over — levels build across the set'],
    ['reset', 'Reset each round — everyone starts at level 1'],
  ] as const) {
    const node = el<HTMLOptionElement>('option', undefined, label);
    node.value = value;
    if ((value === 'carry') === draft.carryXp) node.selected = true;
    carry.append(node);
  }
  carry.addEventListener('change', () => { draft.carryXp = carry.value === 'carry'; });
  field('Experience between rounds', carry);

  const problem = el('p', 'lobby-problem');
  const go = el<HTMLButtonElement>('button', 'lobby-create', 'Create lobby');
  go.addEventListener('click', () => {
    /*
     * Checked through the same function a server would use. The dropdowns can
     * only produce legal values, so this catches the name and the empty pool —
     * and, more to the point, means the screen and the server can never disagree
     * about what is allowed.
     */
    const checked = checkSettings({ ...draft, name: name.value });
    if ('error' in checked) {
      problem.textContent = checked.error;
      return;
    }
    problem.textContent = '';
    hooks.create(checked.settings);
  });

  form.append(problem, go);
}
